import type { DepositStatus, EventType, ReviewCaseStatus, WithdrawalStatus } from '@pf/contracts';
import {
  AlertTriangle,
  ArrowRightLeft,
  Ban,
  BadgeCheck,
  Bookmark,
  CheckCircle2,
  CircleDot,
  Lock,
  Merge,
  PauseCircle,
  ScanLine,
  TimerOff,
  Upload,
  XCircle,
  type LucideIcon,
} from 'lucide-react';

/**
 * The single source of truth for how every state in the system looks and reads.
 *
 * SAD §4 defines two synchronised machines — 9 deposit states and 7 withdrawal states — and the
 * console renders both. Rather than let each screen invent a colour, all 16 states map onto five
 * *meanings*:
 *
 * | tone        | means                                              |
 * |-------------|----------------------------------------------------|
 * | `flight`    | in progress, no action required from anyone yet     |
 * | `attention` | a human must act — nothing auto-resolves from here  |
 * | `good`      | terminal, money moved as intended                   |
 * | `bad`       | terminal, no money moved / claim discredited        |
 * | `idle`      | in the pool, matchable, not yet part of a deposit   |
 *
 * Colour is never the only signal: each state also carries a distinct icon and its own label, so
 * the badges remain distinguishable in greyscale and to colour-blind operators (WCAG 1.4.1).
 *
 * Labels and descriptions are i18n keys rather than strings — `en.json` / `th.json` carry the
 * actual copy under `states.*`, and callers resolve them with `t(meta.labelKey)`.
 */

/** The five semantic tones every state maps onto. */
export type StateTone = 'flight' | 'attention' | 'good' | 'bad' | 'idle';

/** How a single state presents. */
export interface StateMeta {
  /** i18n key for the human label, e.g. `states.deposit.UNDER_REVIEW.label`. */
  readonly labelKey: string;
  /** i18n key for the one-sentence explanation, shown in tooltips and on the timeline. */
  readonly descriptionKey: string;
  /** Semantic tone driving colour. */
  readonly tone: StateTone;
  /** Distinct icon — the non-colour channel. */
  readonly icon: LucideIcon;
  /** True when the machine can never leave this state (SAD §4 "Terminal states"). */
  readonly terminal?: boolean;
  /**
   * Raw fallback label for a state the console does not recognise (`labelKey` is `''` in that
   * case). See the render contract on {@link stateMeta}.
   */
  readonly rawLabel?: string;
}

/** The 9 deposit-transaction states (SAD §4A). */
export const DEPOSIT_STATE_META: Readonly<Record<DepositStatus, StateMeta>> = {
  RESERVED: {
    labelKey: 'states.deposit.RESERVED.label',
    descriptionKey: 'states.deposit.RESERVED.description',
    tone: 'flight',
    icon: Bookmark,
    terminal: false,
  },
  AWAITING_EVIDENCE: {
    labelKey: 'states.deposit.AWAITING_EVIDENCE.label',
    descriptionKey: 'states.deposit.AWAITING_EVIDENCE.description',
    tone: 'flight',
    icon: Upload,
    terminal: false,
  },
  VERIFYING: {
    labelKey: 'states.deposit.VERIFYING.label',
    descriptionKey: 'states.deposit.VERIFYING.description',
    tone: 'flight',
    icon: ScanLine,
    terminal: false,
  },
  UNDER_REVIEW: {
    labelKey: 'states.deposit.UNDER_REVIEW.label',
    descriptionKey: 'states.deposit.UNDER_REVIEW.description',
    tone: 'attention',
    icon: AlertTriangle,
    terminal: false,
  },
  CONFIRMED: {
    labelKey: 'states.deposit.CONFIRMED.label',
    descriptionKey: 'states.deposit.CONFIRMED.description',
    tone: 'good',
    icon: BadgeCheck,
    terminal: false,
  },
  CREDIT_PENDING: {
    labelKey: 'states.deposit.CREDIT_PENDING.label',
    descriptionKey: 'states.deposit.CREDIT_PENDING.description',
    tone: 'flight',
    icon: ArrowRightLeft,
    terminal: false,
  },
  COMPLETED: {
    labelKey: 'states.deposit.COMPLETED.label',
    descriptionKey: 'states.deposit.COMPLETED.description',
    tone: 'good',
    icon: CheckCircle2,
    terminal: true,
  },
  EXPIRED: {
    labelKey: 'states.deposit.EXPIRED.label',
    descriptionKey: 'states.deposit.EXPIRED.description',
    tone: 'bad',
    icon: TimerOff,
    terminal: false,
  },
  REJECTED: {
    labelKey: 'states.deposit.REJECTED.label',
    descriptionKey: 'states.deposit.REJECTED.description',
    tone: 'bad',
    icon: XCircle,
    terminal: true,
  },
};

/** The 7 withdrawal-request states (SAD §4B). */
export const WITHDRAWAL_STATE_META: Readonly<Record<WithdrawalStatus, StateMeta>> = {
  AVAILABLE: {
    labelKey: 'states.withdrawal.AVAILABLE.label',
    descriptionKey: 'states.withdrawal.AVAILABLE.description',
    tone: 'idle',
    icon: CircleDot,
    terminal: false,
  },
  RESERVED: {
    labelKey: 'states.withdrawal.RESERVED.label',
    descriptionKey: 'states.withdrawal.RESERVED.description',
    tone: 'flight',
    icon: Bookmark,
    terminal: false,
  },
  LOCKED: {
    labelKey: 'states.withdrawal.LOCKED.label',
    descriptionKey: 'states.withdrawal.LOCKED.description',
    tone: 'flight',
    icon: Lock,
    terminal: false,
  },
  ON_HOLD: {
    labelKey: 'states.withdrawal.ON_HOLD.label',
    descriptionKey: 'states.withdrawal.ON_HOLD.description',
    tone: 'attention',
    icon: PauseCircle,
    terminal: false,
  },
  FULFILLED: {
    labelKey: 'states.withdrawal.FULFILLED.label',
    descriptionKey: 'states.withdrawal.FULFILLED.description',
    tone: 'good',
    icon: CheckCircle2,
    terminal: true,
  },
  CANCELLED: {
    labelKey: 'states.withdrawal.CANCELLED.label',
    descriptionKey: 'states.withdrawal.CANCELLED.description',
    tone: 'bad',
    icon: Ban,
    terminal: true,
  },
  EXPIRED: {
    labelKey: 'states.withdrawal.EXPIRED.label',
    descriptionKey: 'states.withdrawal.EXPIRED.description',
    tone: 'bad',
    icon: TimerOff,
    terminal: true,
  },
  /**
   * A slice folded back into the withdrawal it was cut from (PF 0047).
   *
   * `tone: 'idle'`, not `'bad'`, and that is the whole point of it having its own entry: the
   * other two terminal-without-a-payout states mean the withdrawer's money went back to their
   * wallet, and this one means it went back into the row above — still locked, still queued,
   * still owed. An operator reading a red badge here would go looking for a refund that never
   * happened. Idle is the honest reading: nothing to do, nothing went wrong.
   */
  MERGED: {
    labelKey: 'states.withdrawal.MERGED.label',
    descriptionKey: 'states.withdrawal.MERGED.description',
    tone: 'idle',
    icon: Merge,
    terminal: true,
  },
};

/**
 * Display-only deposit states — presentation names for facts the 9-state machine deliberately
 * folds together.
 *
 * `CANCELLED` is SAD §4A's `EXPIRED` when the `transaction.expired` event says
 * `PARTICIPANT_RELEASE`: the depositor pressed cancel and handed the slot back, rather than
 * sitting on it until the TTL sweep fired. The machine keeps both on one arrow on purpose (the
 * money path cannot tell them apart), but to the customer — and to the operator fielding their
 * "why does my cancelled deposit say expired?" ticket — they are different stories, and the
 * console tells the one that happened. Same folding pattern as the customer withdrawal states
 * below: callers pass the display status, `stateMeta('deposit', …)` resolves either table.
 */
export const DEPOSIT_DISPLAY_STATE_META: Readonly<Record<string, StateMeta>> = {
  CANCELLED: {
    labelKey: 'states.deposit.CANCELLED.label',
    descriptionKey: 'states.deposit.CANCELLED.description',
    tone: 'bad',
    icon: Ban,
    // Mirrors EXPIRED: a late slip can still reopen the transaction inside the grace window.
    terminal: false,
  },
};

/**
 * The three extra states a *customer's* view of a withdrawal can be in.
 *
 * SAD §4B has seven states and they are all PaymentFlow's. These are the participating website's,
 * covering the window in which funds are locked on its ledger but PaymentFlow has not accepted
 * the request — see `CUSTOMER_WITHDRAWAL_STATUSES` in `@pf/bff-contract`. They appear only in the
 * customer area, never in the operator console, because PaymentFlow genuinely cannot see them.
 */
export const CUSTOMER_WITHDRAWAL_STATE_META: Readonly<Record<string, StateMeta>> = {
  PENDING_SUBMIT: {
    labelKey: 'states.customerWithdrawal.PENDING_SUBMIT.label',
    descriptionKey: 'states.customerWithdrawal.PENDING_SUBMIT.description',
    tone: 'flight',
    icon: CircleDot,
    terminal: false,
  },
  SUBMIT_FAILED: {
    labelKey: 'states.customerWithdrawal.SUBMIT_FAILED.label',
    descriptionKey: 'states.customerWithdrawal.SUBMIT_FAILED.description',
    tone: 'attention',
    icon: AlertTriangle,
    terminal: false,
  },
  ORPHAN_RELEASED: {
    labelKey: 'states.customerWithdrawal.ORPHAN_RELEASED.label',
    descriptionKey: 'states.customerWithdrawal.ORPHAN_RELEASED.description',
    tone: 'idle',
    icon: Ban,
    terminal: true,
  },
};

/** The lifecycle of a manual-review case (maker → checker dual control). */
export const REVIEW_CASE_STATE_META: Readonly<Record<ReviewCaseStatus, StateMeta>> = {
  OPEN: {
    labelKey: 'states.review.OPEN.label',
    descriptionKey: 'states.review.OPEN.description',
    tone: 'attention',
    icon: AlertTriangle,
    terminal: false,
  },
  PROPOSED: {
    labelKey: 'states.review.PROPOSED.label',
    descriptionKey: 'states.review.PROPOSED.description',
    tone: 'flight',
    icon: PauseCircle,
    terminal: false,
  },
  CONFIRMED: {
    labelKey: 'states.review.CONFIRMED.label',
    descriptionKey: 'states.review.CONFIRMED.description',
    tone: 'good',
    icon: BadgeCheck,
    terminal: false,
  },
  CLOSED: {
    labelKey: 'states.review.CLOSED.label',
    descriptionKey: 'states.review.CLOSED.description',
    tone: 'idle',
    icon: CheckCircle2,
    terminal: true,
  },
};

/** Tailwind classes for each tone, used by badges, dots, and timeline rails. */
export const TONE_CLASSES: Readonly<
  Record<StateTone, { badge: string; dot: string; text: string; rail: string }>
> = {
  flight: {
    badge: 'bg-state-flight-bg text-state-flight ring-1 ring-inset ring-state-flight/25',
    dot: 'bg-state-flight',
    text: 'text-state-flight',
    rail: 'bg-state-flight/40',
  },
  attention: {
    badge: 'bg-state-attention-bg text-state-attention ring-1 ring-inset ring-state-attention/30',
    dot: 'bg-state-attention',
    text: 'text-state-attention',
    rail: 'bg-state-attention/40',
  },
  good: {
    badge: 'bg-state-good-bg text-state-good ring-1 ring-inset ring-state-good/25',
    dot: 'bg-state-good',
    text: 'text-state-good',
    rail: 'bg-state-good/40',
  },
  bad: {
    badge: 'bg-state-bad-bg text-state-bad ring-1 ring-inset ring-state-bad/25',
    dot: 'bg-state-bad',
    text: 'text-state-bad',
    rail: 'bg-state-bad/40',
  },
  idle: {
    badge: 'bg-state-idle-bg text-state-idle ring-1 ring-inset ring-state-idle/25',
    dot: 'bg-state-idle',
    text: 'text-state-idle',
    rail: 'bg-state-idle/40',
  },
};

/** Which machine a state belongs to. `RESERVED` and `EXPIRED` exist in both. */
export type StateMachine = 'deposit' | 'withdrawal' | 'review-case';

/** Look up the presentation metadata for a state in a given machine. */
export function stateMeta(machine: 'deposit', status: DepositStatus): StateMeta;
/** Look up the presentation metadata for a state in a given machine. */
export function stateMeta(machine: 'withdrawal', status: WithdrawalStatus): StateMeta;
/** Look up the presentation metadata for a state in a given machine. */
export function stateMeta(machine: 'review-case', status: ReviewCaseStatus): StateMeta;
/** Look up the presentation metadata for a state in a given machine. */
export function stateMeta(machine: StateMachine, status: string): StateMeta;
export function stateMeta(machine: StateMachine, status: string): StateMeta {
  const table =
    machine === 'deposit'
      ? // The display-only folding: `CANCELLED` is how the console names an EXPIRED deposit the
        // depositor released themselves — see DEPOSIT_DISPLAY_STATE_META.
        ({ ...DEPOSIT_STATE_META, ...DEPOSIT_DISPLAY_STATE_META } as Record<
          string,
          StateMeta | undefined
        >)
      : machine === 'withdrawal'
        ? // The website's own pre-pool states are folded in here rather than kept in a fourth
          // machine: to a customer looking at their withdrawal there is one lifecycle, and which
          // system owns each state is not a distinction they should have to make.
          ({ ...WITHDRAWAL_STATE_META, ...CUSTOMER_WITHDRAWAL_STATE_META } as Record<
            string,
            StateMeta | undefined
          >)
        : (REVIEW_CASE_STATE_META as Record<string, StateMeta | undefined>);
  const found = table[status];
  if (found) return found;
  // RENDER CONTRACT: callers must render `meta.labelKey ? t(meta.labelKey) : (meta.rawLabel ?? '')`
  // rather than assuming every state has a translatable label — an unknown state genuinely has no
  // copy to translate, only the raw status string the API sent.
  return {
    labelKey: '',
    rawLabel: status,
    tone: 'idle',
    icon: CircleDot,
    descriptionKey: 'states.unknown.description',
    terminal: false,
  };
}

/**
 * How each of the 14 webhook/domain event types (SAD §9) presents on a state timeline.
 *
 * The timeline is built from the event log rather than from the current status, because the
 * event store is the record of truth (SAD §13) — the status is a projection of it.
 */
/**
 * Internal transitions the operator plane surfaces beyond the SAD §9 catalog. `review.approved`
 * is the one people ask about — "who let this money move" — and hiding it made an approved
 * transaction's timeline end on "sent to review".
 */
export type InternalTimelineEvent =
  | 'review.approved'
  | 'review.rejected'
  /**
   * "Ask the provider again." Emitted when an operator sends a `PROVIDER_DOWN` case back through
   * verification rather than deciding the money by hand — the outage closed the case, the
   * provider's answer never did.
   *
   * It is a *wire* event — `@pf/contracts`' `EVENT_TYPES` carries it — and is named here as well
   * so the console still compiles against a `@pf/contracts` build that predates it. The union
   * de-duplicates, so `EVENT_META` is required to cover it exactly once either way.
   */
  | 'review.reverify_requested'
  | 'transaction.verifying'
  | 'transaction.credit_retry'
  | 'transaction.grace_closed'
  | 'transaction.reopened';

export const EVENT_META: Readonly<
  Record<EventType | InternalTimelineEvent, { labelKey: string; tone: StateTone }>
> = {
  'review.approved': { labelKey: 'states.event.reviewApproved', tone: 'good' },
  'review.rejected': { labelKey: 'states.event.reviewRejected', tone: 'bad' },
  // `flight`, not `attention`: re-asking the provider puts the transaction back *in progress* —
  // nobody has to do anything next, which is the whole point of the button that emits it.
  'review.reverify_requested': {
    labelKey: 'states.event.reviewReverifyRequested',
    tone: 'flight',
  },
  'transaction.verifying': { labelKey: 'states.event.transactionVerifying', tone: 'flight' },
  'transaction.credit_retry': { labelKey: 'states.event.transactionCreditRetry', tone: 'attention' },
  'transaction.grace_closed': { labelKey: 'states.event.transactionGraceClosed', tone: 'idle' },
  'transaction.reopened': { labelKey: 'states.event.transactionReopened', tone: 'attention' },
  'deposit.reserved': { labelKey: 'states.event.depositReserved', tone: 'flight' },
  'withdrawal.reserved': { labelKey: 'states.event.withdrawalReserved', tone: 'flight' },
  'transaction.awaiting_evidence': {
    labelKey: 'states.event.transactionAwaitingEvidence',
    tone: 'flight',
  },
  'reservation.evidence_overdue': {
    labelKey: 'states.event.reservationEvidenceOverdue',
    tone: 'attention',
  },
  'slip.verified': { labelKey: 'states.event.slipVerified', tone: 'flight' },
  'transaction.under_review': {
    labelKey: 'states.event.transactionUnderReview',
    tone: 'attention',
  },
  'transaction.confirmed': { labelKey: 'states.event.transactionConfirmed', tone: 'good' },
  'transaction.completed': { labelKey: 'states.event.transactionCompleted', tone: 'good' },
  'transaction.rejected': { labelKey: 'states.event.transactionRejected', tone: 'bad' },
  'transaction.expired': { labelKey: 'states.event.transactionExpired', tone: 'bad' },
  'withdrawal.fulfilled': { labelKey: 'states.event.withdrawalFulfilled', tone: 'good' },
  'withdrawal.released': { labelKey: 'states.event.withdrawalReleased', tone: 'idle' },
  // The fold home (0047). `idle`, not `good`: no member money moves — a slice's hold returns to
  // the row it was cut from and both locks stay held — so colouring it as a settlement would read
  // as money changing hands on a timeline where none did.
  'withdrawal.slice_merged': { labelKey: 'states.event.withdrawalSliceMerged', tone: 'idle' },
  'webhook.failed': { labelKey: 'states.event.webhookFailed', tone: 'attention' },
  'webhook.dead_lettered': { labelKey: 'states.event.webhookDeadLettered', tone: 'bad' },
  // Phase 3 clearing (SAD §15). These never appear on a transaction timeline — their aggregate
  // is the settlement — but EVENT_META is total over the catalog by type, and totality is the
  // point: adding an event type without deciding its presentation is a compile error.
  'settlement.instructed': { labelKey: 'states.event.settlementInstructed', tone: 'attention' },
  'settlement.completed': { labelKey: 'states.event.settlementCompleted', tone: 'good' },
  'settlement.failed': { labelKey: 'states.event.settlementFailed', tone: 'bad' },
  'settlement.overdue': { labelKey: 'states.event.settlementOverdue', tone: 'bad' },
};

/** Human-readable explanation of each review reason (M-06 / SAD §6). */
export const REVIEW_REASON_META: Readonly<
  Record<string, { labelKey: string; descriptionKey: string; tone: StateTone }>
> = {
  NAME_BAND: {
    labelKey: 'states.reviewReason.NAME_BAND.label',
    descriptionKey: 'states.reviewReason.NAME_BAND.description',
    tone: 'attention',
  },
  AMOUNT_MISMATCH: {
    labelKey: 'states.reviewReason.AMOUNT_MISMATCH.label',
    descriptionKey: 'states.reviewReason.AMOUNT_MISMATCH.description',
    tone: 'attention',
  },
  INCOMPLETE_DATA: {
    labelKey: 'states.reviewReason.INCOMPLETE_DATA.label',
    descriptionKey: 'states.reviewReason.INCOMPLETE_DATA.description',
    tone: 'attention',
  },
  STALE_TIMESTAMP: {
    labelKey: 'states.reviewReason.STALE_TIMESTAMP.label',
    descriptionKey: 'states.reviewReason.STALE_TIMESTAMP.description',
    tone: 'attention',
  },
  ATTEMPTS_EXHAUSTED: {
    labelKey: 'states.reviewReason.ATTEMPTS_EXHAUSTED.label',
    descriptionKey: 'states.reviewReason.ATTEMPTS_EXHAUSTED.description',
    tone: 'attention',
  },
  EVIDENCE_DEADLINE_BREACHED: {
    labelKey: 'states.reviewReason.EVIDENCE_DEADLINE_BREACHED.label',
    descriptionKey: 'states.reviewReason.EVIDENCE_DEADLINE_BREACHED.description',
    tone: 'attention',
  },
  CREDIT_FAILED: {
    labelKey: 'states.reviewReason.CREDIT_FAILED.label',
    descriptionKey: 'states.reviewReason.CREDIT_FAILED.description',
    tone: 'bad',
  },
  LATE_SLIP: {
    labelKey: 'states.reviewReason.LATE_SLIP.label',
    descriptionKey: 'states.reviewReason.LATE_SLIP.description',
    tone: 'attention',
  },
  VERIFICATION_AMBIGUOUS: {
    labelKey: 'states.reviewReason.VERIFICATION_AMBIGUOUS.label',
    descriptionKey: 'states.reviewReason.VERIFICATION_AMBIGUOUS.description',
    tone: 'attention',
  },
  PROVIDER_DOWN: {
    labelKey: 'states.reviewReason.PROVIDER_DOWN.label',
    descriptionKey: 'states.reviewReason.PROVIDER_DOWN.description',
    tone: 'bad',
  },
  /*
   * The same reason under the *domain's* name.
   *
   * `@pf/domain` calls it `PROVIDER_UNAVAILABLE` and `@pf/contracts` calls it `PROVIDER_DOWN`;
   * `routes/ops/triage.ts#toWireReason` translates on the two endpoints this console reads
   * (`/review-queue`, `/review-cases/{id}`), so `PROVIDER_DOWN` is what actually arrives today.
   * The alias is not decoration: `GET /ops/v1/review-cases` (routes/ops/review.ts) types its rows
   * `z.unknown()` and serialises the domain name untranslated, so the raw value is already on the
   * wire somewhere, and a provider-outage backlog rendering with no readable reason is the one
   * failure mode this table exists to prevent. Same story for the deadline reason below.
   */
  PROVIDER_UNAVAILABLE: {
    labelKey: 'states.reviewReason.PROVIDER_DOWN.label',
    descriptionKey: 'states.reviewReason.PROVIDER_DOWN.description',
    tone: 'bad',
  },
  EVIDENCE_DEADLINE_BREACH: {
    labelKey: 'states.reviewReason.EVIDENCE_DEADLINE_BREACHED.label',
    descriptionKey: 'states.reviewReason.EVIDENCE_DEADLINE_BREACHED.description',
    tone: 'attention',
  },
  SENDER_MISMATCH: {
    labelKey: 'states.reviewReason.SENDER_MISMATCH.label',
    descriptionKey: 'states.reviewReason.SENDER_MISMATCH.description',
    tone: 'attention',
  },
  SLIP_ALREADY_CLAIMED: {
    labelKey: 'states.reviewReason.SLIP_ALREADY_CLAIMED.label',
    descriptionKey: 'states.reviewReason.SLIP_ALREADY_CLAIMED.description',
    tone: 'attention',
  },
  DUPLICATE_SLIP: {
    labelKey: 'states.reviewReason.DUPLICATE_SLIP.label',
    descriptionKey: 'states.reviewReason.DUPLICATE_SLIP.description',
    tone: 'bad',
  },
  SELF_MATCH_SUSPECTED: {
    labelKey: 'states.reviewReason.SELF_MATCH_SUSPECTED.label',
    descriptionKey: 'states.reviewReason.SELF_MATCH_SUSPECTED.description',
    tone: 'bad',
  },
  FRAUD_CLASS_REJECT_SHADOW: {
    labelKey: 'states.reviewReason.FRAUD_CLASS_REJECT_SHADOW.label',
    descriptionKey: 'states.reviewReason.FRAUD_CLASS_REJECT_SHADOW.description',
    tone: 'bad',
  },
};

/**
 * The provider-outage reason, under both names the platform uses for it.
 *
 * A case opened because the circuit breaker was shut is not a case about the money — nobody
 * doubted the slip, we simply could not ask. That is the one reason "verify again" is offered
 * for, and the check has to survive whichever spelling the server sends (see the alias in
 * {@link REVIEW_REASON_META}).
 */
export const PROVIDER_UNAVAILABLE_REASONS: ReadonlySet<string> = new Set([
  'PROVIDER_DOWN',
  'PROVIDER_UNAVAILABLE',
]);

/** True when a review case was opened only because the slip provider could not be asked. */
export function isProviderUnavailableReason(reason: string): boolean {
  return PROVIDER_UNAVAILABLE_REASONS.has(reason);
}

/**
 * Presentation for one timeline event type, or `undefined` for a type nobody has described yet
 * — the timeline then falls back to printing the raw type, which is honest rather than broken.
 * The cast is the one place the total record is read with an open-ended key.
 */
export function timelineEventMeta(
  type: string,
): { labelKey: string; tone: StateTone } | undefined {
  return (EVENT_META as Partial<Record<string, { labelKey: string; tone: StateTone }>>)[type];
}
